import { useCallback } from "react";
import { useQueryClient, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const QUERY_KEY_PREFIX = "conversations";
const conversationKeys = {
  all: [QUERY_KEY_PREFIX],
  list: (apiBaseUrl, searchText) => [QUERY_KEY_PREFIX, apiBaseUrl, searchText]
};
const DEFAULT_PAGE_SIZE = 30;
const fetchConversationPage = async (apiBaseUrl, limit, cursor, searchText) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  if (searchText == null ? void 0 : searchText.trim()) params.set("searchText", searchText.trim());
  const response = await fetch(`${apiBaseUrl}/concierge/conversations?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error(`Failed to fetch conversations: ${response.status}`);
  return response.json();
};
const deleteConversationApi = async (apiBaseUrl, conversationId) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations/${conversationId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to delete conversation");
};
const renameConversationApi = async (apiBaseUrl, conversationId, title) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations/${conversationId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify({ title })
  });
  if (!response.ok) throw new Error("Failed to rename conversation");
};
function useConversations({
  apiBaseUrl,
  searchText,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE
}) {
  const queryClient = useQueryClient();
  const queryKey = conversationKeys.list(apiBaseUrl, searchText);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchConversationPage(apiBaseUrl, pageSize, pageParam, searchText),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: void 0,
    enabled,
    staleTime: 3e4,
    gcTime: 10 * 6e4,
    refetchOnMount: "always"
  });
  const allConversations = (data == null ? void 0 : data.pages.flatMap((p) => p.conversations)) ?? [];
  const deleteMutation = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map((id) => deleteConversationApi(apiBaseUrl, id)));
    },
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.all });
      const snapshots = /* @__PURE__ */ new Map();
      const idsToRemove = new Set(ids);
      queryClient.setQueriesData(
        { queryKey: conversationKeys.all },
        (old) => {
          if (!(old == null ? void 0 : old.pages)) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              conversations: page.conversations.filter(
                (c) => !idsToRemove.has(c.conversationId)
              )
            }))
          };
        }
      );
      return { snapshots };
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    }
  });
  const renameMutation = useMutation({
    mutationFn: async ({ conversationId, title }) => {
      await renameConversationApi(apiBaseUrl, conversationId, title);
    },
    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.all });
      queryClient.setQueriesData(
        { queryKey: conversationKeys.all },
        (old) => {
          if (!(old == null ? void 0 : old.pages)) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              conversations: page.conversations.map(
                (c) => c.conversationId === conversationId ? { ...c, title } : c
              )
            }))
          };
        }
      );
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    }
  });
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  }, [queryClient]);
  return {
    conversations: allConversations,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    deleteMutation,
    renameMutation,
    refresh,
    queryKey
  };
}
function useConversationRefresh() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  }, [queryClient]);
}
export {
  conversationKeys,
  useConversationRefresh,
  useConversations
};
//# sourceMappingURL=useConversations.js.map
